import type {
  ActiveContractByCategory,
  AvisoPlanoView,
  ContractCategory,
  ResolverAvisoPlanoInput,
  TrialStatusItem,
} from './types';
import { podeCriarSemContrato } from './estadoCriacao';
import { excedenteDaFranquia, franquiaEsgotada } from './utils/franquiaCiclo';
import { percentualUsado, saldoBaixo, testeAcabando } from './utils/limiaresPlano';
import { diasAte, formatarDiaMes } from './utils/periodoContrato';
import { capitalizarPrimeiraLetra } from '@/shared/utils/text';

import { flexoesDaCategoria, nomeDaCategoria, unidadeDaCategoria } from './utils/rotulosCategoria';

export const ROTA_ABA_PLANO = '/configuracoes/financeiro/plano';

function contarFormatado(valor: number): string {
  return valor.toLocaleString('pt-BR');
}

function avisoSemPlano(categoria: ContractCategory): AvisoPlanoView {
  const unidade = unidadeDaCategoria(categoria);
  const { possessivo, novos } = flexoesDaCategoria(categoria);
  return {
    variant: 'semPlano',
    title: `Você não tem um plano de ${nomeDaCategoria(categoria)}`,
    description: `Pode consultar e baixar ${possessivo} ${unidade}. Para criar ${novos}, contrate o produto.`,
    actionLabel: 'Ver planos',
  };
}

function avisoSemConvenio(): AvisoPlanoView {
  return {
    variant: 'semConvenio',
    title: 'Sem convênio de Arbitragem',
    description:
      'Você pode abrir processos normalmente; cada um tem custa avulsa, cobrada depois da triagem. Com um convênio, o consumo entra no plano.',
    actionLabel: 'Ver planos',
  };
}

// Sem contrato, a Arbitragem ainda cria (custa avulsa): dizer "contrate para criar" seria falso.
function avisoSemContrato(categoria: ContractCategory): AvisoPlanoView {
  return podeCriarSemContrato(categoria) ? avisoSemConvenio() : avisoSemPlano(categoria);
}

function avisoTesteTerminou(categoria: ContractCategory, dataFim: string): AvisoPlanoView {
  const unidade = unidadeDaCategoria(categoria);
  const { possessivo, novos } = flexoesDaCategoria(categoria);
  const depoisDoTeste = podeCriarSemContrato(categoria)
    ? `${capitalizarPrimeiraLetra(novos)} ${unidade} passam a ter custa avulsa, cobrada depois da triagem.`
    : 'Para voltar a criar, ative um plano.';
  return {
    variant: 'trialTerminou',
    title: `Seu teste terminou em ${dataFim}`,
    description: `${capitalizarPrimeiraLetra(possessivo)} ${unidade} continuam aqui para consulta. ${depoisDoTeste}`,
    actionLabel: 'Ativar plano',
  };
}

function avisoTesteAcabando(
  categoria: ContractCategory,
  dataFim: string,
  diasRestantes: number,
): AvisoPlanoView {
  const unidade = unidadeDaCategoria(categoria);
  const { novos } = flexoesDaCategoria(categoria);
  const description = podeCriarSemContrato(categoria)
    ? `Depois de ${dataFim}, ${novos} ${unidade} passam a ter custa avulsa. Ative um plano para continuar no convênio.`
    : `Depois de ${dataFim} você não conseguirá criar ${novos} ${unidade}. Ative um plano para continuar.`;
  return {
    variant: 'trialAcabando',
    title:
      diasRestantes <= 0 ? 'Seu teste termina hoje' : `Seu teste termina em ${diasRestantes} dias`,
    description,
    actionLabel: 'Ativar plano',
  };
}

function avisoSaldoBaixo(
  categoria: ContractCategory,
  restantes: number,
  percentual: number,
  renovaEm: string,
): AvisoPlanoView {
  const unidade = unidadeDaCategoria(categoria);
  const quandoRenova = renovaEm ? ` Ele renova em ${renovaEm}.` : '';
  return {
    variant: 'saldoBaixo',
    title: `Restam ${contarFormatado(restantes)} ${unidade} no seu plano`,
    description: `Você já usou ${percentual}% da franquia deste ciclo.${quandoRenova}`,
    actionLabel: 'Ver meu plano',
  };
}

function avisoEsgotado(categoria: ContractCategory, renovaEm: string): AvisoPlanoView {
  const unidade = unidadeDaCategoria(categoria);
  const { novos } = flexoesDaCategoria(categoria);
  const ate = renovaEm ? ` até ${renovaEm}, quando o ciclo renova` : ' até o ciclo renovar';
  return {
    variant: 'esgotado',
    title: 'Você usou todo o saldo deste ciclo',
    description: `Não é possível criar ${novos} ${unidade}${ate}.`,
    actionLabel: 'Ver meu plano',
  };
}

function avisoFranquiaEstourada(categoria: ContractCategory, excedente: number): AvisoPlanoView {
  const unidade = unidadeDaCategoria(categoria);
  const { artigo, proximos } = flexoesDaCategoria(categoria);

  if (excedente <= 0) {
    return {
      variant: 'franquiaEstourada',
      title: 'Você chegou ao fim da franquia do ciclo',
      description: `Pode continuar criando: ${artigo} ${proximos} ${unidade} entram na fatura como excedente.`,
      actionLabel: 'Ver consumo',
    };
  }

  return {
    variant: 'franquiaEstourada',
    title: 'Você passou da franquia do ciclo',
    description: `Pode continuar criando: ${artigo} ${contarFormatado(excedente)} ${unidade} acima da franquia entram na próxima fatura.`,
    actionLabel: 'Ver consumo',
  };
}

function avisoDoTeste(
  categoria: ContractCategory,
  contrato: ActiveContractByCategory,
  itemTeste: TrialStatusItem | null,
): AvisoPlanoView | null {
  if (contrato.status !== 'TRIAL' && contrato.status !== 'EXPIRED') return null;

  if (itemTeste) {
    const dataFim = formatarDiaMes(itemTeste.trialEnd);
    if (itemTeste.marco === 'ENCERRADO') return avisoTesteTerminou(categoria, dataFim);
    return avisoTesteAcabando(categoria, dataFim, itemTeste.diasRestantes ?? 0);
  }

  if (!contrato.trialEnd) return null;

  const dataFim = formatarDiaMes(contrato.trialEnd);
  if (!dataFim) return null;

  if (contrato.status === 'EXPIRED') return avisoTesteTerminou(categoria, dataFim);
  if (testeAcabando(contrato)) {
    return avisoTesteAcabando(categoria, dataFim, diasAte(contrato.trialEnd));
  }

  return null;
}

function avisoDoCiclo(
  categoria: ContractCategory,
  contrato: ActiveContractByCategory,
): AvisoPlanoView | null {
  const periodo = contrato.currentPeriod;
  const total = periodo?.totalBalance ?? null;
  if (!periodo || total === null) return null;

  const renovaEm = periodo.periodEnd ? formatarDiaMes(periodo.periodEnd) : '';

  if (franquiaEsgotada(periodo)) {
    return contrato.paymentMode === 'postpaid'
      ? avisoFranquiaEstourada(categoria, excedenteDaFranquia(periodo))
      : avisoEsgotado(categoria, renovaEm);
  }

  if (!saldoBaixo(periodo)) return null;

  return avisoSaldoBaixo(
    categoria,
    total - periodo.usedBalance,
    percentualUsado(periodo),
    renovaEm,
  );
}

export function resolverAvisoPlano({
  categoria,
  contrato,
  contratoResolvido,
  itemTeste,
  isGuestWorkspace,
}: ResolverAvisoPlanoInput): AvisoPlanoView | null {
  if (isGuestWorkspace || !contratoResolvido || !contrato) return null;

  if (!contrato.hasContract) return avisoSemContrato(categoria);

  const aviso = avisoDoTeste(categoria, contrato, itemTeste);
  if (aviso) return aviso;

  if (!contrato.hasActive) return avisoSemContrato(categoria);
  if (contrato.status === 'TRIAL') return null;

  return avisoDoCiclo(categoria, contrato);
}
